import {
  ChartFrame,
  Donut,
  Legend,
  LineLayer,
  stackExtent,
  StackedAreaLayer,
  ZeroLine,
} from '@finsim/charts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountSeries, seriesColor } from '../lib/series';
import { ChartCard } from './chart-card';
import type { CanvasData } from './use-canvas';

const NET_WORTH = 'var(--text-1)';

/** Assets (up) and liabilities (down) by account, with net worth; allocation at the cursor. */
export function Balance({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const { project, focused, focusedResult, valueMode, fmt, range, dates, selectedItemId } = data;
  const [hover, setHover] = useState(-1);

  const series = useMemo(() => {
    if (!focusedResult || !focused) return [];
    const scenario = project.scenarios.find((s) => s.id === focused.id);
    const accounts = accountSeries(
      project,
      scenario?.itemIds ?? [],
      focusedResult,
      valueMode,
      (name) => t('charts.owedFor', { name }),
    );
    return [
      { id: 'cash', label: t('charts.cash'), values: focusedResult.series.cash[valueMode] },
      ...accounts,
    ].map((s, i) => ({
      ...s,
      color: seriesColor(i),
      muted:
        selectedItemId !== null &&
        s.id !== selectedItemId &&
        accounts.some((a) => a.id === selectedItemId),
    }));
  }, [project, focused, focusedResult, valueMode, selectedItemId, t]);

  if (!focused || !focusedResult) return null;
  const netWorth = focusedResult.series.netWorth[valueMode];
  const [lo, hi] = stackExtent(
    series.map((s) => s.values),
    dates.length,
  );
  const at = hover >= range[0] && hover <= range[1] ? hover : range[1];
  const slices = series.map((s) => ({
    id: s.id,
    label: s.label,
    value: s.values[at] ?? 0,
    color: s.color,
  }));
  const holdings = slices.filter((s) => s.value > 0);
  const total = holdings.reduce((a, s) => a + s.value, 0);
  const atDate = dates[at];

  return (
    <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <ChartCard
        title={t('charts.balanceTitle')}
        subtitle={[focused.name, data.moneyCaption].filter(Boolean).join(' · ')}
        legend={
          <Legend
            label={t('charts.balanceTitle')}
            items={[
              ...series.map((s) => ({ id: s.id, label: s.label, color: s.color })),
              { id: 'nw', label: t('charts.netWorth'), color: NET_WORTH },
            ]}
          />
        }
        table={{
          caption: t('charts.balanceTitle'),
          columns: [t('charts.year'), ...series.map((s) => s.label), t('charts.netWorth')],
          rows: data.buckets.map((b) => ({
            label: String(b.year),
            values: [
              ...series.map((s) => fmt.money(s.values[b.to] ?? 0)),
              fmt.money(netWorth[b.to] ?? 0),
            ],
          })),
        }}
      >
        <ChartFrame
          markers={data.markers}
          dates={dates}
          range={range}
          height={340}
          domainSeries={[lo, hi, netWorth]}
          formatY={fmt.compact}
          formatXTick={fmt.year}
          ariaLabel={t('charts.balanceAria', { name: focused.name })}
          hoverIndex={hover}
          onHoverChange={setHover}
          renderTooltip={(i) => (
            <div className="space-y-1">
              <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
              {series.map((s) =>
                (s.values[i] ?? 0) === 0 ? null : (
                  <p key={s.id} className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-1.5 text-text-2">
                      <span
                        aria-hidden
                        className="size-2 rounded-sm"
                        style={{ background: s.color }}
                      />
                      {s.label}
                    </span>
                    <span className="tabular">{fmt.money(s.values[i] ?? 0)}</span>
                  </p>
                ),
              )}
              <p className="flex justify-between gap-4 border-t border-border-subtle pt-1 font-medium">
                <span>{t('charts.netWorth')}</span>
                <span className="tabular">{fmt.money(netWorth[i] ?? 0)}</span>
              </p>
            </div>
          )}
        >
          <StackedAreaLayer series={series} />
          <ZeroLine />
          <LineLayer values={netWorth} color={NET_WORTH} />
        </ChartFrame>
      </ChartCard>

      <section className="rounded-lg border border-border-subtle bg-bg-1 px-4 pt-3 pb-3">
        <h2 className="text-sm font-medium">{t('charts.allocation')}</h2>
        <p className="mb-3 text-xs text-text-3">{atDate ? fmt.monthLong(atDate) : ''}</p>
        <div className="flex justify-center">
          <Donut
            size={160}
            slices={slices}
            label={t('charts.allocationAria', { date: atDate ? fmt.monthLong(atDate) : '' })}
          />
        </div>
        <ul className="mt-3 space-y-1.5 text-sm">
          {holdings.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 text-text-2">
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-sm"
                  style={{ background: s.color }}
                />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="tabular shrink-0 text-xs">
                {fmt.money(s.value)}{' '}
                <span className="text-text-3">· {fmt.percent(total ? s.value / total : 0)}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
